import React, { useState } from 'react';
import { List, useTable } from '@refinedev/antd';
import { Table, Space, Tag, Input, Select, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const SEVERITIES = ['WARNING', 'BLOCK', 'FLAG'];

interface AuditRow {
  id: string;
  userId: string;
  rule: string;
  severity: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  user: {
    id: string;
    email?: string | null;
    phone?: string | null;
    fullName?: string | null;
  };
}

const severityColor = (severity: string) => {
  if (severity === 'BLOCK') return 'red';
  if (severity === 'WARNING') return 'orange';
  if (severity === 'FLAG') return 'gold';
  return 'default';
};

export const GamificationAuditList: React.FC = () => {
  const [userIdFilter, setUserIdFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string | undefined>();
  const [ruleFilter, setRuleFilter] = useState('');

  const { tableProps } = useTable<AuditRow>({
    resource: 'gamification-audit',
    filters: {
      permanent: [
        ...(userIdFilter
          ? [{ field: 'userId', operator: 'eq' as const, value: userIdFilter }]
          : []),
        ...(severityFilter
          ? [{ field: 'severity', operator: 'eq' as const, value: severityFilter }]
          : []),
        ...(ruleFilter
          ? [{ field: 'rule', operator: 'eq' as const, value: ruleFilter }]
          : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List title="Gamification Audit">
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Filter by user ID"
          value={userIdFilter}
          onChange={(e) => setUserIdFilter(e.target.value.trim())}
          style={{ width: 280 }}
          allowClear
        />
        <Select
          placeholder="Severity"
          allowClear
          style={{ width: 160 }}
          value={severityFilter}
          onChange={setSeverityFilter}
          options={SEVERITIES.map((s) => ({ value: s, label: s }))}
        />
        <Input
          placeholder="Rule (e.g. RAPID_COMPLETION)"
          value={ruleFilter}
          onChange={(e) => setRuleFilter(e.target.value.trim())}
          style={{ width: 240 }}
          allowClear
        />
      </Space>

      <Table {...tableProps} rowKey="id" scroll={{ x: 900 }}>
        <Table.Column
          title="Time"
          dataIndex="createdAt"
          width={160}
          render={(v: string) => dayjs(v).format('YYYY-MM-DD HH:mm')}
        />
        <Table.Column
          title="User"
          width={220}
          render={(_: unknown, row: AuditRow) => (
            <div>
              <div style={{ fontWeight: 500 }}>{row.user.fullName || '—'}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.user.email || row.user.phone || row.userId}
              </Text>
            </div>
          )}
        />
        <Table.Column title="Rule" dataIndex="rule" width={180} />
        <Table.Column
          title="Severity"
          dataIndex="severity"
          width={120}
          render={(severity: string) => (
            <Tag color={severityColor(severity)}>{severity}</Tag>
          )}
        />
        <Table.Column
          title="Metadata"
          dataIndex="metadata"
          ellipsis
          render={(v: Record<string, unknown> | null) =>
            v ? JSON.stringify(v) : '—'
          }
        />
      </Table>
    </List>
  );
};
